/**
 * Calendar panel wrapper — switches between Manual and SelfSchedule calendar modes.
 * Fetches interviewer calendar events and passes them as external events.
 * Exposes CalendarHandle via forwardRef for programmatic navigation (goToDate).
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Flex, Spinner } from '@chakra-ui/react';
import moment from 'moment-timezone';
import type { EventInput } from '@fullcalendar/core';
import {
  useInterviewerCalendarChunksQuery,
  CalendarEventsFetchError,
  type PrefetchWeeks,
} from '@/api/calendarEvents';
import { useInterviewerSchedulePrefetch } from '@/api/interviewerSchedule';
import { useShowToast } from '@/hooks';
import type { CalendarHandle } from '@kula-ai/components';
import type { ScheduleMode } from '../config/schedule.types';
import type { ScheduleSlot } from '../state/useScheduleSharedState';
import type { BusyEvent, InterviewKind } from '../availability';
import {
  useAvailableSlots,
  useCommonAvailability,
  useMeetingRoomBusyEvents,
  useMinimumNoticeOverlay,
  useUnavailableHoursOverlay,
  type ManualCalendarExternalEvent,
} from './hooks';
import { getPastelSlotColorsForUser } from './interviewerPastelColors';
import { ManualCalendar } from './ManualCalendar';
import { SelfScheduleCalendar } from './SelfScheduleCalendar';
import { NoSlotsAvailablePopover } from './NoSlotsAvailablePopover';
import { syncScheduleInterviewerPastelKeys } from '../state/interviewerPastelMap';

interface CalendarPanelProps {
  mode: ScheduleMode;
  /** Drives union (1:1) vs intersection (panel) for common availability. */
  interviewKind?: InterviewKind;
  duration: number;
  timezone?: string;
  interviewerIds?: string[];
  meetingRoomIds?: string[];
  /** Canonical room identifiers (emails) — used for the room busy fetch. */
  meetingRoomEmails?: string[];
  /** `email → friendly room label` — powers the busy-event title /
   *  tooltip so users see "Mercedes" not the raw meeting subject. */
  meetingRoomNamesByEmail?: Record<string, string>;
  interviewName?: string;
  candidateName?: string;
  bookedSlot?: ScheduleSlot | null;
  onSlotSelect?: (_slot: { start: string; end: string }) => void;
  onTimezoneChange?: (_tz: string) => void;
  onSlotsChange?: (_slots: Array<{ start: string; end: string }>) => void;
  /**
   * Fires whenever the visible week anchor changes (navigation + tz
   * changes). Consumers use this to drive per-week fetches that live
   * outside the calendar (interviewer workload, dropdown week range).
   */
  onVisibleWeekStartChange?: (_iso: string) => void;
  /**
   * Fires with the subset of interviewer IDs whose calendar isn't
   * connected (busy fetch flagged `calendar_available: false`). The
   * Details form uses this to render a warning icon on each affected
   * chip.
   */
  onCalendarAvailabilityChange?: (_disconnectedIds: number[]) => void;
  isEdit?: boolean;
  /** How many ISO weeks to load from the visible week anchor (default 2) */
  prefetchWeeks?: PrefetchWeeks;
  /** Extra weeks beyond `prefetchWeeks` to background-prefetch (next+next navigation) */
  lookaheadPrefetchWeeks?: number;
  /** Calendar events API — forwarded as `ats_job_id` when set */
  atsJobId?: number;
  /** Hours of lead time required before the earliest bookable slot. */
  minimumNoticeHours?: number;
}

function fallbackVisibleWeekStartIso(tz: string): string {
  return moment().tz(tz).startOf('isoWeek').startOf('day').toISOString();
}

export const CalendarPanel = forwardRef<CalendarHandle, CalendarPanelProps>(
  (
    {
      mode,
      interviewKind = 'one_on_one',
      duration,
      timezone,
      interviewerIds = [],
      meetingRoomIds = [],
      meetingRoomEmails = [],
      meetingRoomNamesByEmail = {},
      interviewName,
      candidateName,
      bookedSlot,
      onSlotSelect,
      onTimezoneChange,
      onSlotsChange,
      onVisibleWeekStartChange,
      onCalendarAvailabilityChange,
      isEdit,
      prefetchWeeks = 2,
      lookaheadPrefetchWeeks = 1,
      atsJobId,
      minimumNoticeHours = 0,
    },
    ref
  ) => {
    const toast = useShowToast();
    const isSelfSchedule =
      mode === 'self-schedule' || mode === 'self-schedule-adhoc';

    const resolvedTz =
      timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
    const isLocalTimezone = resolvedTz === 'local';

    const [activeVisibleStartIso, setActiveVisibleStartIso] = useState<
      string | null
    >(null);

    const handleActiveRangeChange = useCallback(
      (range: { start: string; end: string }) => {
        const startIso = moment
          .tz(range.start, resolvedTz)
          .startOf('day')
          .toISOString();
        setActiveVisibleStartIso(startIso);
      },
      [resolvedTz]
    );

    const { visibleWeekStartIso } = useMemo(() => {
      const tz = resolvedTz;
      const visibleStart =
        activeVisibleStartIso ?? fallbackVisibleWeekStartIso(tz);
      const startM = moment(visibleStart).tz(tz).startOf('day');
      return {
        visibleWeekStartIso: startM.toISOString(),
      };
    }, [activeVisibleStartIso, resolvedTz]);

    const visibleWeekEndIso = useMemo(
      () =>
        moment(visibleWeekStartIso).tz(resolvedTz).add(7, 'days').toISOString(),
      [resolvedTz, visibleWeekStartIso]
    );

    useEffect(() => {
      onVisibleWeekStartChange?.(visibleWeekStartIso);
    }, [onVisibleWeekStartChange, visibleWeekStartIso]);

    const numericInterviewerIds = useMemo(
      () =>
        interviewerIds
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id) && id > 0),
      [interviewerIds]
    );

    const sortedInterviewerIds = useMemo(
      () => [...numericInterviewerIds].sort((a, b) => a - b),
      [numericInterviewerIds]
    );

    const {
      mergedEvents: calendarEvents,
      isBlockingBusyFetch,
      fetchError,
      usersInfoById,
    } = useInterviewerCalendarChunksQuery({
      userIds: numericInterviewerIds,
      anchorWeekStartIso: visibleWeekStartIso,
      timezone: resolvedTz,
      prefetchWeeks,
      lookaheadPrefetchWeeks,
      ...(atsJobId !== null && atsJobId > 0 ? { atsJobId } : {}),
      enabled: !isSelfSchedule && numericInterviewerIds.length > 0,
      isLocalTimezone,
    });

    const lastDisconnectedKeyRef = useRef<string>('');
    useEffect(() => {
      if (!onCalendarAvailabilityChange) return;
      const disconnected: number[] = [];
      for (const id of sortedInterviewerIds) {
        const info = usersInfoById[String(id)];
        if (info && info.calendar_available === false) disconnected.push(id);
      }
      // `sortedInterviewerIds` gets a fresh reference each render (the
      // parent re-maps ids in-place), so comparing by content avoids a
      // feedback loop where every emit triggers a shared-state update
      // that re-renders CalendarPanel and re-emits.
      const key = disconnected.join(',');
      if (key === lastDisconnectedKeyRef.current) return;
      lastDisconnectedKeyRef.current = key;
      onCalendarAvailabilityChange(disconnected);
    }, [onCalendarAvailabilityChange, sortedInterviewerIds, usersInfoById]);

    const lastToastKeyRef = useRef<string | null>(null);

    useEffect(() => {
      if (
        !fetchError ||
        fetchError.name === 'AbortError' ||
        fetchError.name === 'CanceledError'
      ) {
        return;
      }
      const key = `${fetchError.name}:${fetchError.message}`;
      if (lastToastKeyRef.current === key) {
        return;
      }
      lastToastKeyRef.current = key;
      const description =
        fetchError instanceof CalendarEventsFetchError
          ? fetchError.userMessage
          : fetchError.message;
      toast({
        title: 'Calendar',
        description,
        status: 'error',
        duration: 8000,
      });
    }, [fetchError, toast]);

    const calendarEventsForVisibleWeek = useMemo(() => {
      const vs = moment(visibleWeekStartIso).valueOf();
      const ve = moment(visibleWeekEndIso).valueOf();
      // In edit mode the interviewer's real Google Calendar already
      // carries THIS interview as an event; dropping it stops the
      // calendar from rendering the slot twice (once from our injected
      // bookedSlot overlay, once from the interviewer busy fetch).
      // Match legacy KInterviewScheduler heuristic (useCalendarEvents
      // line 443): exact start + end + subject.
      const editBookedStart =
        isEdit && bookedSlot?.start ? moment(bookedSlot.start).valueOf() : null;
      const editBookedEnd =
        isEdit && bookedSlot?.end ? moment(bookedSlot.end).valueOf() : null;
      return calendarEvents.filter((evt) => {
        const s = moment(evt.start).valueOf();
        const e = moment(evt.end).valueOf();
        if (!Number.isFinite(s) || !Number.isFinite(e)) {
          return false;
        }
        if (s >= ve || e <= vs) return false;
        if (
          editBookedStart !== null &&
          editBookedEnd !== null &&
          s === editBookedStart &&
          e === editBookedEnd &&
          typeof evt.title === 'string' &&
          evt.title === interviewName
        ) {
          return false;
        }
        return true;
      });
    }, [
      calendarEvents,
      visibleWeekEndIso,
      visibleWeekStartIso,
      isEdit,
      bookedSlot?.start,
      bookedSlot?.end,
      interviewName,
    ]);

    const externalEvents = useMemo((): ManualCalendarExternalEvent[] => {
      syncScheduleInterviewerPastelKeys(sortedInterviewerIds);
      const seen = new Set<string>();
      const rows: ManualCalendarExternalEvent[] = [];
      for (const evt of calendarEventsForVisibleWeek) {
        const uidRaw = evt.user_id;
        const uid =
          typeof uidRaw === 'number' && Number.isFinite(uidRaw)
            ? uidRaw
            : sortedInterviewerIds[0];
        if (
          typeof uid === 'number' &&
          Number.isFinite(uid) &&
          !sortedInterviewerIds.includes(uid)
        ) {
          continue;
        }
        const dedupeKey = `${evt.id ?? ''}|${evt.start}|${evt.end}|${uid ?? ''}`;
        if (seen.has(dedupeKey)) {
          continue;
        }
        seen.add(dedupeKey);
        const colors = getPastelSlotColorsForUser(sortedInterviewerIds, uid);
        rows.push({
          id: evt.id ?? `${evt.start}-${evt.end}-${uid ?? ''}`,
          start: evt.start,
          end: evt.end,
          title: evt.title ?? 'Busy',
          backgroundColor: colors.backgroundColor,
          /** KInterviewScheduler parity: border matches fill (`borderColor: light`) — no dark outline ring. */
          borderColor: colors.backgroundColor,
          textColor: colors.textColor,
        });
      }
      return rows;
    }, [calendarEventsForVisibleWeek, sortedInterviewerIds]);

    // --- Available-slot overlay (manual only) --------------------------
    const interviewerBusyForAlgorithm = useMemo<BusyEvent[]>(() => {
      const rows: BusyEvent[] = [];
      for (const evt of calendarEventsForVisibleWeek) {
        const uidRaw = evt.user_id;
        const uid =
          typeof uidRaw === 'number' && Number.isFinite(uidRaw)
            ? uidRaw
            : sortedInterviewerIds[0];
        if (uid === undefined) continue;
        rows.push({
          id: String(evt.id ?? `${evt.start}-${evt.end}-${uid}`),
          start: evt.start,
          end: evt.end,
          title: evt.title,
          interviewerId: uid,
        });
      }
      return rows;
    }, [calendarEventsForVisibleWeek, sortedInterviewerIds]);

    const {
      events: meetingRoomBusyEvents,
      externalEvents: meetingRoomExternalEvents,
      isBlockingBusyFetch: isRoomBusyBlocking,
    } = useMeetingRoomBusyEvents({
      roomEmails: meetingRoomEmails,
      roomNamesByEmail: meetingRoomNamesByEmail,
      visibleWeekStartIso,
      visibleWeekEndIso,
      timezone: resolvedTz,
      ...(atsJobId !== undefined ? { atsJobId } : {}),
      prefetchWeeks,
      lookaheadPrefetchWeeks,
      enabled:
        !isSelfSchedule && !isLocalTimezone && meetingRoomEmails.length > 0,
    });

    // Both humans and rooms must be free for a slot to qualify.
    const busyEventsForAlgorithm = useMemo<BusyEvent[]>(
      () => [...interviewerBusyForAlgorithm, ...meetingRoomBusyEvents],
      [interviewerBusyForAlgorithm, meetingRoomBusyEvents]
    );

    // Overlay spinner while either the interviewer or meeting-room
    // busy fetch is still resolving the visible week.
    const showBusyEventsLoader =
      !isSelfSchedule &&
      !isLocalTimezone &&
      ((numericInterviewerIds.length > 0 && isBlockingBusyFetch) ||
        isRoomBusyBlocking);

    const { availability } = useCommonAvailability({
      weekStartIso: visibleWeekStartIso,
      timezone: resolvedTz,
      interviewerIds: sortedInterviewerIds,
      interviewKind,
      enabled:
        !isSelfSchedule && !isLocalTimezone && sortedInterviewerIds.length > 0,
    });

    // Silently warm React Query with the next few weeks of working
    // hours so forward navigation hits a cache instead of the network.
    useInterviewerSchedulePrefetch({
      anchorWeekStartIso: visibleWeekStartIso,
      timezone: resolvedTz,
      interviewerIds: sortedInterviewerIds,
      prefetchWeeks,
      lookaheadPrefetchWeeks,
      enabled:
        !isSelfSchedule && !isLocalTimezone && sortedInterviewerIds.length > 0,
    });

    // Once the user has picked a slot, hide the "Available" overlay —
    // it's visual noise at that point and matches legacy behaviour.
    const isSlotBooked = Boolean(bookedSlot?.start && bookedSlot?.end);

    // A window is only offered as "Available" when it fits the whole
    // interview. Shorter gaps are dropped by the gap-finder so the
    // hover preview never has to visualise an unbookable slot.
    const { events: availableSlotEvents, slots: availableSlotsRaw } =
      useAvailableSlots({
        availability: isSlotBooked ? [] : availability,
        busyEvents: busyEventsForAlgorithm,
        timezone: resolvedTz,
        minSlotMinutes: duration,
        minimumNoticeHours,
      });

    // Show the "No slots available this week" banner once the gap-
    // finder has settled (availability loaded, no slot booked yet) and
    // produced zero bookable slots. Avoids flashing while busy events
    // are still arriving from the background poll.
    const showNoSlotsPopover =
      !isSelfSchedule &&
      !isSlotBooked &&
      sortedInterviewerIds.length > 0 &&
      availability.length > 0 &&
      !isBlockingBusyFetch &&
      availableSlotsRaw.length === 0;

    const minimumNoticeEvents = useMinimumNoticeOverlay({
      noticeHours: minimumNoticeHours,
      timezone: resolvedTz,
      visibleWeekStartIso,
      visibleWeekEndIso,
    });

    const unavailableHoursEvents = useUnavailableHoursOverlay({
      availability,
      timezone: resolvedTz,
      visibleWeekStartIso,
      visibleWeekEndIso,
      enabled: !isSelfSchedule && sortedInterviewerIds.length > 0,
    });

    const overlayEvents = useMemo<EventInput[]>(() => {
      if (isSelfSchedule) return [];
      return [
        ...unavailableHoursEvents,
        ...minimumNoticeEvents,
        ...availableSlotEvents,
      ];
    }, [
      isSelfSchedule,
      unavailableHoursEvents,
      minimumNoticeEvents,
      availableSlotEvents,
    ]);

    // Interviewer pastel busy events + grey meeting-room busy events
    // share the same `externalEvents` slot on ManualCalendar.
    const calendarExternalEvents = useMemo<ManualCalendarExternalEvent[]>(
      () => [...externalEvents, ...meetingRoomExternalEvents],
      [externalEvents, meetingRoomExternalEvents]
    );

    return (
      <Flex w="100%" h="100%" minH={0} flexDir="column" overflow="visible">
        {isSelfSchedule ? (
          <SelfScheduleCalendar
            duration={duration}
            timezone={timezone}
            interviewerIds={interviewerIds}
            meetingRoomIds={meetingRoomIds}
            onSlotsChange={onSlotsChange}
          />
        ) : (
          <Box position="relative" w="100%" h="100%" minH={0} flex="1">
            <ManualCalendar
              ref={ref}
              duration={duration}
              timezone={timezone}
              interviewName={interviewName}
              candidateName={candidateName}
              bookedSlot={bookedSlot}
              externalEvents={calendarExternalEvents}
              overlayEvents={overlayEvents}
              onSlotSelect={onSlotSelect ?? (() => {})}
              onTimezoneChange={onTimezoneChange}
              onActiveRangeChange={handleActiveRangeChange}
              isEdit={isEdit}
            />
            <NoSlotsAvailablePopover
              // Reset the dismissed state whenever the roster or the
              // visible week changes so the banner can reappear.
              key={`${visibleWeekStartIso}|${sortedInterviewerIds.join(',')}`}
              visible={showNoSlotsPopover}
            />
            {showBusyEventsLoader ? (
              <Flex
                position="absolute"
                inset={0}
                align="center"
                justify="center"
                bg="whiteAlpha.800"
                zIndex={20}
                pointerEvents="none"
                borderRadius="md"
              >
                <Spinner size="lg" color="secondary.500" thickness="3px" />
              </Flex>
            ) : null}
          </Box>
        )}
      </Flex>
    );
  }
);

CalendarPanel.displayName = 'CalendarPanel';
